import {
  basicLogger,
  initialize,
  LDClient,
  LDMultiKindContext,
  LDSingleKindContext,
} from 'launchdarkly-js-client-sdk';
import { accessControl } from 'lib-shared-access-control';
import { config } from 'lib-shared-backend-config';
import {
  defaultFeatureFlags,
  FeatureFlags as FeatureFlagsT,
} from 'lib-shared-feature-flags';
import { Sentry } from 'lib-shared-sentry';

import { kebabToCamelCase } from 'utils/formatting/string';

import {
  FeatureFlagName,
  FeatureFlagResult,
  FeatureFlagsOrganization,
  FeatureFlagsUser,
  InitResolvedValue,
} from './featureFlags.types';

/**
 * Represents the feature flags setup.
 *
 * This encapsulates the logic and communication with LaunchDarkly as well as
 * feature flag evaluation and tracking.
 */
class FeatureFlags {
  /**
   * The evaluated flags.
   *
   * We keep track of this to avoid spamming LaunchDarkly with identical
   * evaluations of the same flag. Essentially we only evaluate once per
   * session, i.e. until the agent refreshes the page.
   */
  private readonly evaluated = new Set<string>();
  /** Whether the flags have been initialized. */
  private initialized = false;
  /** The LaunchDarkly client. */
  private client: LDClient | null = null;
  /** The feature flags. */
  private flags: FeatureFlagsT | null = { ...defaultFeatureFlags };

  /**
   * (Re)Initializes the LaunchDarkly client.
   *
   * @param user The user.
   * @param organization The organization.
   */
  async init(
    user: FeatureFlagsUser,
    organization: FeatureFlagsOrganization,
  ): Promise<InitResolvedValue> {
    this.deleteUnusedFlagsFromLocalStorage();

    return new Promise<InitResolvedValue>((resolve) => {
      if (this.client) {
        this.client.close().then(() => {
          this.setupClient(user, organization);
          this.setupClientEventListeners({ validate: false, resolve });
        });
      } else {
        this.setupClient(user, organization);
        this.setupClientEventListeners({ validate: true, resolve });
      }
    });
  }

  /**
   * Gets the feature flags.
   *
   * @param names The names.
   */
  get<TFeatureFlagName extends FeatureFlagName>(
    names: readonly TFeatureFlagName[],
  ): FeatureFlagResult<TFeatureFlagName> {
    if (!this.initialized) {
      Sentry.captureMessage(
        `Evaluating feature flags before initialization. Falling back to default flags.`,
        {
          level: 'warning',
          extra: {
            flags: names,
          },
        },
      );

      if (DEVELOPMENT) {
        console.error(
          `The feature flags "${names.join(
            ', ',
          )}" are being evaluated before initialization. Falling back to default flags.\n\nThis is likely a bug and should be fixed. Check that you call "featureFlags.get" inside the app (e.g. a component) after it has been initialized by "useAppInit".`,
        );
      }
    }

    this.track(names);
    return names.reduce(
      (acc, name) => ({ ...acc, [kebabToCamelCase(name)]: this.flags?.[name] }),
      {} as FeatureFlagResult<TFeatureFlagName>,
    );
  }

  /**
   * Tracks the variations.
   *
   * @param names The names.
   */
  track<TFeatureFlagName extends FeatureFlagName>(
    names: readonly TFeatureFlagName[],
  ): void {
    names
      .filter((name) => !this.evaluated.has(name))
      .forEach((name) => {
        if (this.client) {
          this.client.variation(name);
          this.evaluated.add(name);
        }
      });
  }

  /**
   * Provide role field mapped from custom roles to launchdarkly for the flags
   * which rely on it. It is not a reliable solution and will work only with
   * System defined roles. It is meant to be used temprorarily until proper
   * solution to avoid role usage in launchdarkly is implemented.
   */
  private getUserRole() {
    if (accessControl.roles?.includes('Admin')) {
      return 'admin';
    }
    if (accessControl.roles?.includes('System Admin')) {
      return 'systemadmin';
    }
    if (accessControl.roles?.includes('Team Lead')) {
      return 'teamlead';
    }

    return 'agent';
  }

  /**
   * Sets up the LaunchDarkly client.
   *
   * @param user The user.
   * @param organization The organization.
   */
  private setupClient(
    user: FeatureFlagsUser,
    organization: FeatureFlagsOrganization,
  ): void {
    const userContext: LDSingleKindContext = {
      kind: 'user',
      key: user.id,
      email: user.email ?? undefined,
      name: `${organization.name}: user`,
      // TODO: [CORE-850] Remove role usage from launchdarkly
      role: this.getUserRole(),

      /**
       * The following properties have been moved to the `organization` context,
       * and should be removed from the `user` context as soon as feature flag
       * targeting rules are migrated in LaunchDarkly.
       */
      organizationID: organization.id,
      organizationDomain: organization.subdomain,
      trialEndedAt: organization?.trial_ended_at
        ? organization?.trial_ended_at
        : '',
      isInTrial: typeof organization.trial_ended_at === 'string' ? false : true,

      /**
       * The following properties have been moved to the `developer` context,
       * and should be removed from the `user` context as soon as feature flag
       * targeting rules are migrated in LaunchDarkly.
       */
      appVersion: VERSION,
      appBuildTime: TIMESTAMP,
      hostname: window.location.hostname,

      _meta: {
        privateAttributes: ['email'],
      },
    };

    const organizationContext: LDSingleKindContext = {
      kind: 'organization',
      key: organization.id,
      name: organization.name,
      subdomain: organization.subdomain,
      trialEndedAt: organization?.trial_ended_at
        ? organization?.trial_ended_at
        : '',
      isInTrial: typeof organization.trial_ended_at === 'string' ? false : true,
    };

    const developerContext: LDSingleKindContext = {
      kind: 'developer',
      key: `developer:${user.id}`,
      appVersion: VERSION,
      appBuildTime: TIMESTAMP,
      hostname: window.location.hostname,
    };

    this.client = initialize(
      config.sdk.launchDarkly,
      {
        kind: 'multi',
        user: userContext,
        organization: organizationContext,
        developer: developerContext,
      },
      {
        bootstrap: 'localStorage',
        sendEvents: true,
        sendEventsOnlyForVariation: true,
        logger: basicLogger({ level: 'warn' }),
      },
    );
  }

  /*
   * Clear feature flags saved in local storage for old builds.
   */
  private deleteUnusedFlagsFromLocalStorage(): void {
    Object.keys(localStorage)
      .filter((key) => /ld:\w+:(?!\$)\w+/.test(key))
      .forEach((key) => {
        const [_ld, token, userEncodedB64] = key.split(':');
        const contexts: LDMultiKindContext = JSON.parse(atob(userEncodedB64));

        const { appVersion, appBuildTime } =
          (contexts.developer as LDSingleKindContext) ?? {};

        if (
          token === config.sdk.launchDarkly &&
          (appVersion !== VERSION || appBuildTime !== TIMESTAMP)
        ) {
          localStorage.removeItem(key);
        }
      });
  }

  /**
   * Sets up the LaunchDarkly event listeners.
   *
   * @param options The listener options.
   */
  private setupClientEventListeners(options: {
    /** Whether or not to validate the flags. */
    validate: boolean;
    /** The resolve executor function to signal readiness. */
    resolve: (arg: InitResolvedValue) => void;
  }): void {
    if (this.client) {
      // The ready is event will always trigger regardless of whether it
      // successfully connected to LaunchDarkly.
      this.client.on('ready', () => {
        if (options.validate) {
          this.validateFlags();
        }

        this.initialized = true;
        this.flags = { ...defaultFeatureFlags, ...this.client?.allFlags() };

        if (DEVELOPMENT) {
          try {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { featureFlagOverrides } = require('feature-flags.local');
            this.flags = { ...this.flags, ...featureFlagOverrides };
          } catch {
            // local-overrides.ts does not exist — no overrides applied
          }
        }

        options.resolve({ flags: this.flags! });

        this.client?.on('change', () => {
          // Because we've set bootstrap to localStorage, LaunchDarkly will
          // attempt to fetch the flags from persistent storage in order to make
          // them immediately available on page load. Meanwhile, after the ready
          // event, the client makes a background request to LaunchDarkly to fetch
          // the latest values and will subsequently store them in localStorage.
          // We subscribe to the change event with a noop so that LaunchDarkly
          // always syncs localStorage with the latest values behind the scenes
          // while the application is running. Thus when the browser is reloaded
          // it will always be bootstrapped with the most recent set of flags.
        });
      });
    }
  }

  /**
   * Warns about LaunchDarkly not being able to fetch the flags and against
   * discrepancies between the server flags and the default flags.
   */
  private validateFlags(): void {
    if (this.client) {
      const flags = this.client.allFlags();

      if (!Object.keys(flags).length) {
        console.warn(
          'There was an error connecting to LaunchDarkly. Falling back to default flags.',
          defaultFeatureFlags,
        );
      } else {
        const missingDefaultFlags = Object.keys(flags).filter(
          (flag) => typeof defaultFeatureFlags[flag] === 'undefined',
        );
        const missingLaunchDarklyFlags = Object.keys(
          defaultFeatureFlags,
        ).filter((flag) => typeof flags[flag] === 'undefined');

        const isDevelopment = process.env.NODE_ENV === 'development';

        if (
          isDevelopment &&
          (missingDefaultFlags.length || missingLaunchDarklyFlags.length)
        ) {
          console.warn(
            `There is a discrepancy between the LaunchDarkly and default feature flags.\n`,
            missingDefaultFlags.length
              ? `- LaunchDarkly has flags without defaults:\n\t${missingDefaultFlags.join(
                  ',\n\t',
                )}\n`
              : '',
            missingLaunchDarklyFlags.length
              ? `- The fallback has flags not in LaunchDarkly:\n\t${missingLaunchDarklyFlags.join(
                  ',\n\t',
                )}`
              : '',
          );
        }
      }
    }
  }
}

export const featureFlags = new FeatureFlags();
